import { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import { useTagStore } from 'src/modules/tag/TagStore';
import { Tag } from 'src/types/tag';
import { getTextColorForBackground, toUpperCaseColor } from 'src/utils/color';
import { DEFAULT_TAG_COLOR } from 'src/constants/tag';

interface TagSelectProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  taskId?: string | null;
  onUpdate?: (tags: Tag[]) => Promise<void> | void;
  disabled?: boolean;
}

const TagSelect: React.FC<TagSelectProps> = ({
  value,
  onChange,
  taskId,
  onUpdate,
  disabled = false,
}) => {
  const { tags: tagOptions, loadTags } = useTagStore();
  const [query, setQuery] = useState('');
  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value);
      }, 300),
    [],
  );

  const options = useMemo(
    () =>
      tagOptions.map((tag) => ({
        _id: tag._id,
        name: tag.name,
        color: toUpperCaseColor(tag.color) || DEFAULT_TAG_COLOR,
        createdAt: tag.createdAt,
        updatedAt: tag.updatedAt,
      })),
    [tagOptions],
  );

  useEffect(() => {
    loadTags(query, 1, 25);
  }, [query, loadTags]);

  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  const handleInputChange = useCallback(
    (_: unknown, value: string, reason: string) => {
      if (reason !== 'input' && reason !== 'clear') {
        return;
      }

      const nextValue = reason === 'clear' ? '' : value.trim();

      debouncedSetQuery(nextValue);
    },
    [debouncedSetQuery],
  );

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (newTags: Tag[]) => {
        if (taskId && onUpdate) {
          await onUpdate(newTags);
        }
      }, 500),
    [onUpdate, taskId],
  );

  const handleChange = (_: unknown, newValue: Tag[]) => {
    const normalized = newValue.map((tag) => ({
      ...tag,
      color: toUpperCaseColor(tag.color) || DEFAULT_TAG_COLOR,
    }));

    onChange(normalized);
    debouncedUpdate(normalized);
  };

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const displayedValue = useMemo(
    () =>
      value.map(
        (tag) =>
          options.find((option) => option._id === tag._id) || {
            ...tag,
            color: toUpperCaseColor(tag.color) || DEFAULT_TAG_COLOR,
          },
      ),
    [value, options],
  );

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={options}
      value={displayedValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, val) => option._id === val._id}
      filterOptions={(tagOptions) => tagOptions}
      renderInput={(params) => <TextField {...params} placeholder="Tags" />}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            label={option.name || option._id}
            sx={{
              backgroundColor: option.color || DEFAULT_TAG_COLOR,
              color: getTextColorForBackground(
                option.color || DEFAULT_TAG_COLOR,
              ),
              '& .MuiChip-deleteIcon': {
                color: getTextColorForBackground(
                  option.color || DEFAULT_TAG_COLOR,
                ),
                '&:hover': {
                  color: getTextColorForBackground(
                    option.color || DEFAULT_TAG_COLOR,
                  ),
                },
              },
            }}
          />
        ))
      }
    />
  );
};

export default observer(TagSelect);
